// src/pages/NomCatalogosPage.jsx
// Catálogos de nómina: Departamentos, Puestos y Registros Patronales en pestañas
import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerDepartamentos, crearDepartamento, actualizarDepartamento, eliminarDepartamento,
  obtenerPuestos, crearPuesto, actualizarPuesto, eliminarPuesto,
  obtenerRegPatronales, crearRegPatronal, actualizarRegPatronal, eliminarRegPatronal,
} from "../services/nominaService";

const TABS = ["Departamentos", "Puestos", "Registros Patronales"];

// ===== Componente genérico de catálogo simple (clave + nombre) =====
function SimpleCatalog({ rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ Clave: "", Nombre: "", Activo: true });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row ? { Clave: row.clave ?? row.Clave ?? "", Nombre: row.nombre ?? row.Nombre ?? "", Activo: row.activo ?? row.Activo ?? true } : { Clave: "", Nombre: "", Activo: true });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(editRow?.id ?? editRow?.Id ?? null, form);
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await onDelete(id);
      toastSuccess("Eliminado");
    } catch {
      toastError("No se pudo eliminar");
    }
  };

  const columns = [
    { key: "clave", title: "Clave", style: { width: 120, fontWeight: 600 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "activo", title: "Activo",
      render: (r) => (r.activo ?? r.Activo)
        ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>,
      style: { width: 70, textAlign: "center" },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id ?? r.Id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 420, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar" : "Nuevo"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Clave *</label>
                <input className="lmd-input" value={form.Clave} onChange={(e) => setForm((f) => ({ ...f, Clave: e.target.value }))}
                  required style={{ width: "100%" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Nombre *</label>
                <input className="lmd-input" value={form.Nombre} onChange={(e) => setForm((f) => ({ ...f, Nombre: e.target.value }))}
                  required style={{ width: "100%" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
                <input type="checkbox" checked={form.Activo} onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.checked }))} />
                Activo
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Catálogo de Registros Patronales =====
function RegPatronalCatalog({ rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ NumeroRegistro: "", Nombre: "", FactorRiesgoPuesto: "0.0054", Activo: true });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row
      ? { NumeroRegistro: row.numeroRegistro, Nombre: row.nombre, FactorRiesgoPuesto: String(row.factorRiesgoPuesto), Activo: row.activo }
      : { NumeroRegistro: "", Nombre: "", FactorRiesgoPuesto: "0.0054", Activo: true });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(editRow?.id ?? null, { ...form, FactorRiesgoPuesto: parseFloat(form.FactorRiesgoPuesto) || 0.0054 });
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await onDelete(id);
      toastSuccess("Eliminado");
    } catch {
      toastError("No se pudo eliminar");
    }
  };

  const columns = [
    { key: "numeroRegistro", title: "No. Registro", style: { fontWeight: 600, width: 160 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "factorRiesgoPuesto", title: "Factor Riesgo",
      render: (r) => Number(r.factorRiesgoPuesto).toFixed(4),
      style: { width: 120, textAlign: "right" },
    },
    {
      key: "activo", title: "Activo",
      render: (r) => r.activo ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span> : <span style={{ color: "#cbd5e1" }}>—</span>,
      style: { width: 70, textAlign: "center" },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 460, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar Registro Patronal" : "Nuevo Registro Patronal"}</h3>
            <form onSubmit={handleSubmit}>
              {[
                { label: "No. Registro IMSS *", key: "NumeroRegistro", placeholder: "Y00-00000-00-0" },
                { label: "Nombre / Descripción *", key: "Nombre" },
                { label: "Factor de Riesgo de Trabajo", key: "FactorRiesgoPuesto", type: "number", placeholder: "0.0054" },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>{label}</label>
                  <input className="lmd-input" type={type || "text"} value={form[key]} required={label.includes("*")}
                    placeholder={placeholder}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ width: "100%" }} />
                </div>
              ))}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
                <input type="checkbox" checked={form.Activo} onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.checked }))} />
                Activo
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== PÁGINA PRINCIPAL =====
export default function NomCatalogosPage() {
  const [activeTab, setActiveTab] = useState(0);

  const [deptos, setDeptos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [regPat, setRegPat] = useState([]);
  const [loadingD, setLoadingD] = useState(false);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingR, setLoadingR] = useState(false);

  const loadDeptos = useCallback(async () => {
    setLoadingD(true);
    try { setDeptos(await obtenerDepartamentos()); } finally { setLoadingD(false); }
  }, []);

  const loadPuestos = useCallback(async () => {
    setLoadingP(true);
    try { setPuestos(await obtenerPuestos()); } finally { setLoadingP(false); }
  }, []);

  const loadReg = useCallback(async () => {
    setLoadingR(true);
    try { setRegPat(await obtenerRegPatronales()); } finally { setLoadingR(false); }
  }, []);

  useEffect(() => { loadDeptos(); loadPuestos(); loadReg(); }, [loadDeptos, loadPuestos, loadReg]);

  const saveDepto = async (id, form) => {
    if (id) { await actualizarDepartamento(id, form); } else { await crearDepartamento(form); }
    loadDeptos();
  };

  const savePuesto = async (id, form) => {
    if (id) { await actualizarPuesto(id, form); } else { await crearPuesto(form); }
    loadPuestos();
  };

  const saveReg = async (id, form) => {
    if (id) { await actualizarRegPatronal(id, form); } else { await crearRegPatronal(form); }
    loadReg();
  };

  const deleteDepto = async (id) => { await eliminarDepartamento(id); loadDeptos(); };
  const deletePuesto = async (id) => { await eliminarPuesto(id); loadPuestos(); };
  const deleteReg = async (id) => { await eliminarRegPatronal(id); loadReg(); };

  return (
    <div>
      <PageHeader title="Catálogos de Nómina" subtitle="Departamentos, puestos y registros patronales" />

      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--clr-border)", marginBottom: 20 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setActiveTab(i)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            fontSize: 14, fontWeight: activeTab === i ? 700 : 400,
            color: activeTab === i ? "var(--clr-primary)" : "var(--clr-text-muted)",
            borderBottom: activeTab === i ? "2px solid var(--clr-primary)" : "2px solid transparent",
            marginBottom: -2, transition: "all .2s",
          }}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 0 && (
        <SimpleCatalog rows={deptos} loading={loadingD} onSave={saveDepto} onDelete={deleteDepto} />
      )}
      {activeTab === 1 && (
        <SimpleCatalog rows={puestos} loading={loadingP} onSave={savePuesto} onDelete={deletePuesto} />
      )}
      {activeTab === 2 && (
        <RegPatronalCatalog rows={regPat} loading={loadingR} onSave={saveReg} onDelete={deleteReg} />
      )}
    </div>
  );
}
